import { Helmet } from "react-helmet-async";

const BASE_URL = "https://martinofranco.com";
const DEFAULT_IMAGE = `${BASE_URL}/img/og-default.webp`;

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  publishedTime?: string;
  tags?: string[];
  author?: string;
  /** JSON-LD structured data (Schema.org). Se inyecta como <script type="application/ld+json"> */
  jsonLd?: Record<string, any>;
}

export default function SEOHead({
  title = "Franco Martino",
  description = "Franco Martino, desarrollador Full Stack especializado en React, Python, automatización e IoT.",
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  publishedTime,
  tags = [],
  author = "Franco Martino",
  jsonLd,
}: SEOHeadProps) {
  const fullTitle =
    title === "Franco Martino"
      ? title
      : `${title} | Franco Martino`;

  // Asegura que la imagen siempre sea una URL absoluta
  const absoluteImage = image.startsWith("http")
    ? image
    : `${BASE_URL}/${image.replace(/^\//, "")}`;

  const canonicalUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  return (
    <Helmet>
      <html lang="es" prefix="og: http://ogp.me/ns#" />
      {/* Básicos */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph (Facebook, LinkedIn, WhatsApp, Telegram…) */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Franco Martino" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="es_AR" />

      {/* Twitter / X Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Artículo (solo en blog posts) */}
      {type === "article" && <meta name="author" content={author} />}
      {type === "article" && <meta property="article:author" content={author} />}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" &&
        tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
